import type { FamilySearchResultResource } from "@/api/generated";
import { familyDiscoveryQueries } from "@/features/family-discovery/familyDiscovery.queries";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useDebounce } from "use-debounce";

const MIN_QUERY_LENGTH = 2;

export type FamilySearchResult = FamilySearchResultResource;

export function useFamilyDiscoverySearch() {
  const [query, setQuery] = useState("");
  const [selectedFamily, setSelectedFamily] = useState<FamilySearchResult | null>(null);
  const trimmedQuery = query.trim();
  const [debouncedQuery] = useDebounce(trimmedQuery, 350);
  const canSearch = trimmedQuery.length >= MIN_QUERY_LENGTH;
  const canQuery = debouncedQuery.length >= MIN_QUERY_LENGTH;
  const searchQuery = useQuery({
    ...familyDiscoveryQueries.search(debouncedQuery),
    enabled: canQuery && !selectedFamily,
  });
  const familyCode = selectedFamily?.url || selectedFamily?.short_code || "";
  const appDeepLink = familyCode ? `awraq://family/${encodeURIComponent(familyCode)}` : null;

  const updateQuery = (value: string) => {
    setQuery(value);
    setSelectedFamily(null);
  };

  const selectFamily = (family: FamilySearchResult) => {
    setSelectedFamily(family);
  };

  return {
    appDeepLink,
    canSearch,
    families: searchQuery.data ?? [],
    familyCode,
    hasError: searchQuery.isError,
    isLoading: canSearch && !selectedFamily && (debouncedQuery !== trimmedQuery || searchQuery.isFetching),
    query,
    selectedFamily,
    selectFamily,
    updateQuery,
  };
}
